import { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Alert,
  CardContent,
} from "@mui/material";

//components
import Title from "../../components/Typography/Title";
import Texas100RoadSegmentsMap from "../../components/Map/Texas100RoadSegmentsMap";
import SubHeader from "../../components/Header/SubHeader";

//custom hook
import { useFetch } from "../../hooks/useFetch";

//constants
import { API_URLS, CONTACT_CARDS } from "../../Constants";

//styles
import {
  StyledGridContainer,
  StyledGrid,
  StyledCard
} from "./styles";
import themes from "../../themes/defaultTheme";

export default function HomePage() {
  const [top100RoadwaysData, setTop100RoadwaysData] = useState([]);
  const [featureId, setFeatureId] = useState(null);
  const layerRefs = useRef({});
  const roadwaysFetcher = useFetch("get", `${API_URLS["GET_TEXAS100_MONITORED_ROADWAYS"]}?minRank=0&maxRank=100`);

  useEffect(() => {
    roadwaysFetcher.setRequest({});
  }, []);

  //Once response is received, set appropriate state variables.
  useEffect(() => {
    const response = roadwaysFetcher.serverResponse;
    if (response?.length > 0) {
      setTop100RoadwaysData(response || []);
    } else if (response?.error) {
      console.error("Error in GET roadways request", response.error);
    }
  }, [roadwaysFetcher.serverResponse]);

  //Invoked when a road segment is selected.
  const handleRoadClick = (feature) => {
    setFeatureId(feature.id);
  };

  //Defines per-feature popup content and click handler for each road segment.
  function onEachRoad(feature, layer) {
    const { road: roadName, from_road: fromRoad, to_road: toRoad, arank: rank } = feature.properties;
    const segmentId = feature.id;

    layerRefs.current[segmentId] = layer;

    const primaryColor = themes.default.palette.primary.main;
    const fontFamily = themes.default.typography.fontFamily;
    const visuallyHidden = "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;";
    layer.bindPopup(`
      <div style="min-width:160px;font-family:${fontFamily}">
        <h3 id="popup-title-${segmentId}" style="text-align:center;font-weight:bold;margin:0 0 8px;font-size:1rem">
          Road Segment Details<span style="${visuallyHidden}">.</span>
        </h3>        
        <div id="popup-desc-${segmentId}">
          <p style="margin:4px 0;font-size:0.75rem"><strong>Roadname:</strong> ${roadName}<span style="${visuallyHidden}">.</span></p>
          <p style="margin:4px 0;font-size:0.75rem"><strong>From Road:</strong> ${fromRoad}<span style="${visuallyHidden}">.</span></p>
          <p style="margin:4px 0;font-size:0.75rem"><strong>To Road:</strong> ${toRoad}<span style="${visuallyHidden}">.</span></p>
          <p style="margin:4px 0;font-size:0.75rem"><strong>Rank:</strong> ${rank}<span style="${visuallyHidden}">.</span></p>
        </div>        
        <div style="text-align:center;padding-top:8px">
          <button 
            id="btn-${segmentId}"
            aria-labelledby="popup-title-${segmentId} popup-desc-${segmentId} btn-${segmentId}"
            style="background-color:${primaryColor};color:#fff;border:none;padding:5px 14px;border-radius:4px;cursor:pointer;font-size:0.8125rem;font-family:${fontFamily}">
            View Related Plans
          </button>
        </div>
      </div>
  `, { autoPan: false });

    layer.on("popupopen", () => {
      const popupEl = layer.getPopup().getElement();
      if (!popupEl) return;
      const actionBtn = popupEl.querySelector(`#btn-${segmentId}`);
      if (actionBtn) {
        actionBtn.onclick = () => handleViewRelatedPlansBtnClick(segmentId);
        actionBtn.focus({ preventScroll: true });
      }
    });

    layer.on({
      click: handleRoadClick.bind(null, feature),
    });
  }

  //triggers when View Related Plans button is clicked
  const handleViewRelatedPlansBtnClick = (segmentId) => {
    const backendPageUrl = `${window.location.origin}/pages/roadway/${segmentId}`;

    // Open the backend URL in a new tab
    window.open(backendPageUrl, "_blank");
  };

  return (
    <>
      <SubHeader displayBtns={["mitigationPlans", "tx100OfficialSite", "login"]} />
      <StyledGridContainer container>

        {/* About heading */}
        <Grid component="section" item xs={12} sx={{ textAlign: "left", paddingBottom: 2 }}>
          <Title title="About" />
          <Typography variant="body1">
            In response to increased roadway congestion throughout Texas, since 2009 the Texas Legislature has mandated that TxDOT annually produce a ranked list of the 100 most congested road segments in the state.
            As part of that mandate, the Texas Legislature also mandates that a congestion mitigation plan be drafted in coordination with the local transportation agencies for all road segments in the top 100. These plans, updated quarterly, include any projects that may improve congestion and mobility—including alternatives to highway construction or projects on other road segments that may have an impact on the top 100 segment.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            This site provides access to those mitigation plans by rank and region as well as the system used to create and manage those plans.
          </Typography>
        </Grid>

        {/* For more information heading */}
        <Grid
          component="section"
          item
          container
          xs={12}
          columnSpacing={2}
          sx={{ textAlign: "left" }}
        >
          <Grid item xs={12}>
            <Title title="For more information" />
          </Grid>

          {/* Contact cards */}
          {CONTACT_CARDS?.length > 0 && CONTACT_CARDS.map((contact, index) => (
            <StyledGrid key={index} item xs={4} >
              <StyledCard>
                <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: "bold" }}>
                    {contact.name}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    {contact.agency}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: themes.default.palette.secondary.dark }}
                  >
                    {contact.email}
                  </Typography>

                  <Typography variant="body2">
                    {contact.phoneNumber}
                  </Typography>
                </CardContent>
              </StyledCard>
            </StyledGrid>
          ))}
        </Grid>

        {/* Map */}
        <StyledGrid item xs={12}>
          {!roadwaysFetcher.isLoading && (
            <Alert role="status" severity="info" sx={{ marginBottom: "8px" }}>
              Click on any road segment on the map, or use the search dropdown below to select a segment and view its mitigation plans.
            </Alert>
          )}
          <Texas100RoadSegmentsMap
            data={top100RoadwaysData}
            isLoading={roadwaysFetcher.isLoading}
            onEachFeature={onEachRoad}
            featureId={featureId}
            layerRefs={layerRefs}
            onFeatureSelect={setFeatureId}
          />
        </StyledGrid>
      </StyledGridContainer >
    </>
  );
}
